import { useState } from 'react';
import { useCandidateExperiences } from './useCandidateExperiences';
import { useCandidateContacts } from './useCandidateContacts';
import { calculateWizardCompletion } from '../utils/wizard-progress';
import type { WizardCompletion } from '../utils/wizard-progress';
import type { ContactsByExperience } from '../utils/contact-results';
import type { WizardStepId } from '../types/candidate-wizard.types';
import type { Experience } from '@/features/experiences/types/experience.types';

interface CandidateContactState {
  contactsByExperience: ContactsByExperience;
  loadingExperienceIds: string[];
  isLoading: boolean;
  contactsError: boolean;
  retryContacts: () => void;
}

interface CandidateWizardState extends CandidateContactState {
  experiences: Experience[];
  activeStepId: WizardStepId;
  completion: WizardCompletion;
  goToStep: (stepId: WizardStepId) => void;
}

function getContactState(
  contacts: ReturnType<typeof useCandidateContacts>,
  experiencesLoading: boolean
): CandidateContactState {
  return {
    contactsByExperience: contacts.contactsByExperience,
    loadingExperienceIds: contacts.loadingExperienceIds,
    isLoading: experiencesLoading || contacts.isLoading,
    contactsError: contacts.isError,
    retryContacts: contacts.refetch,
  };
}

export function useCandidateWizard(): CandidateWizardState {
  const { experiences, isLoading } = useCandidateExperiences();
  const [activeStepId, goToStep] = useState<WizardStepId>('experience');
  const list = experiences ?? [];
  const contacts = useCandidateContacts(list.map((experience) => experience.id));
  return {
    experiences: list,
    activeStepId,
    completion: calculateWizardCompletion(list, contacts.contactCounts),
    goToStep,
    ...getContactState(contacts, isLoading),
  };
}
